import React, { useRef, useState, useEffect } from 'react';
import * as Yup from 'yup';
import i18n from 'i18n-js';
import { Grid, Select, MenuItem, FormControl, InputLabel } from '@material-ui/core';
import { BaseModal } from 'components/UI';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import { TextField, MaterialSelectField } from 'components/FormFields';
import { createStripeCreditCardToken, createStripeBankToken } from 'utils';
import useStyle from './AddStripePaymentMethodModal.styled';
import Cards from 'react-credit-cards';
import * as paymentActions from 'state/modules/payment/actions';
import { uiActions } from 'state/modules/UI';
import { FETCH_STATE } from 'constants';

const PAYMENTS = { CREDIT_CARD: 'CREDIT_CARD', ACH: 'ACH' };

const AddStripePaymentMethodModal = ({ open, accountId, handleClick, type }) => {
  const brokers = useSelector((state) => state.config?.settings?.core?.payment?.brokers);
  const fetchAddPaymentMethod = useSelector((state) => state.payment?.fetchAddPaymentMethod);
  const classes = useStyle();
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const [paymentType, setPaymentType] = useState('');
  const [stripeError, setStripeError] = useState(null);
  const [validatingStripe, setValidatingStripe] = useState(false);
  const [achVerify, setAchVerify] = useState(false);

  const loading = fetchAddPaymentMethod === FETCH_STATE.LOADING || validatingStripe;

  const paymentSchemas = {
    [PAYMENTS.CREDIT_CARD]: Yup.object().shape({
      name: Yup.string().required(i18n.t('common.required_field')),
      cvc: Yup.string().required(i18n.t('common.required_field')),
      expiry: Yup.string()
        .max(7, i18n.t('common.invalid_value'))
        .min(7, i18n.t('common.invalid_value'))
        .required(i18n.t('common.required_field')),
      number: Yup.string().required(i18n.t('common.required_field')),
      zipcode: Yup.string().required(i18n.t('common.required_field')),
    }),
    [PAYMENTS.ACH]: Yup.object().shape({
      account_holder_type: Yup.string().required(i18n.t('common.required_field')),
      account_holder_name: Yup.string().required(i18n.t('common.required_field')),
      routing_number: Yup.string().required(i18n.t('common.required_field')),
      account_number: Yup.string().required(i18n.t('common.required_field')),
    }),
  };

  const handleCallback = (issuer, setFieldValue) => {
    let data;
    switch (issuer) {
      case 'mastercard': {
        data = 'MasterCard';
        break;
      }
      case 'visa': {
        data = 'Visa';
        break;
      }
      case 'amex': {
        data = 'AmericanExpress';
        break;
      }
      case 'discover': {
        data = 'Discover';
        break;
      }
      default:
        data = issuer;
        break;
    }

    setFieldValue('field_creditCardType', data || '');
  };

  const normalizeExpiryField = (e) => {
    const value = e?.target?.value;

    if (!value) {
      return formRef.current.setFieldValue('expiry', '');
    }

    const onlyNumbers = value.replace(/[^\d]/g, '');

    if (onlyNumbers.length <= 2) {
      return formRef.current.setFieldValue('expiry', `${onlyNumbers}`);
    }
    if (onlyNumbers.length >= 3) {
      return formRef.current.setFieldValue(
        'expiry',
        `${onlyNumbers.slice(0, 2)}/${onlyNumbers.slice(2)}`
      );
    }

    return formRef.current.setFieldValue('expiry', onlyNumbers);
  };

  const handlePaymentTypeChange = (event) => {
    const newPayment = event?.target?.value;
    setPaymentType(newPayment);
  };

  const handleSave = () => {
    formRef.current.submitForm();
  };

  useEffect(() => {
    if (fetchAddPaymentMethod === FETCH_STATE.DONE) {
      switch (paymentType) {
        case PAYMENTS.CREDIT_CARD: {
          handleClick();
          break;
        }
        case PAYMENTS.ACH: {
          setAchVerify(true);
          break;
        }
        default: {
          handleClick();
          break;
        }
      }
      dispatch(paymentActions.resetFetchPaymentMethods());
    }
  }, [fetchAddPaymentMethod, handleClick]);

  const onSubmit = async (params) => {
    let stripeResponse, payload;
    setStripeError(null);

    const publicKey = brokers?.organization?.STP?.public_key || brokers?.STP?.public_key;

    switch (paymentType) {
      case PAYMENTS.CREDIT_CARD: {
        setValidatingStripe(true);
        stripeResponse = await createStripeCreditCardToken(params, publicKey);
        payload = { token: stripeResponse.id, account_id: accountId };
        break;
      }
      case PAYMENTS.ACH: {
        stripeResponse = await createStripeBankToken(params, publicKey);
        payload = { token: stripeResponse.id, account_id: accountId, broker_type: 'STP' };
        break;
      }
      default: {
        break;
      }
    }
    if (payload?.token) {
      dispatch(paymentActions.fetchAddPaymentMethod(payload));
    } else {
      if (stripeResponse?.error) {
        setStripeError(stripeResponse.error.message);
      }
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: i18n.t('payments.stripe_error'),
        })
      );
    }
    setValidatingStripe(false);
  };

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton={!achVerify}
      hasCancelButton={!achVerify}
      loading={loading}
      onEnter={handleSave}
      onCancel={handleClick}
      enableCloseButton={achVerify}
      handleClick={handleClick}
      enterButtonLabel={i18n.t('common.add')}
      title={achVerify ? i18n.t('payments.verify_title') : i18n.t('payments.add_payment_method')}
      maxWidth={415}
    >
      {achVerify ? (
        <p>{i18n.t('payments.verify_description')}</p>
      ) : (
        <>
          <FormControl fullWidth>
            <InputLabel className={classes.label} id="paymentType">
              {paymentType ? i18n.t('common.type') : i18n.t('common.select_type')}
            </InputLabel>
            <Select
              labelId="paymentType"
              id="paymentType-select"
              defaultValue=" "
              value={paymentType}
              onChange={(event) => handlePaymentTypeChange(event)}
              className={classes.paymentSelect}
              style={{
                marginBottom: '2.5rem',
              }}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
              }}
            >
              {Object.keys(PAYMENTS).map((payment) => (
                <MenuItem key={payment} value={payment}>
                  {i18n.t(`payments.types.${payment}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Formik
            innerRef={formRef}
            initialValues={{}}
            validationSchema={paymentSchemas[paymentType]}
            onSubmit={onSubmit}
          >
            {(props) =>
              InputType[paymentType]
                ? InputType[paymentType]({
                    ...props,
                    classes,
                    normalizeExpiryField,
                    handleCallback,
                  })
                : null
            }
          </Formik>
          {stripeError ? <p class={classes.stripeError}>{stripeError}</p> : null}{' '}
        </>
      )}
    </BaseModal>
  );
};

const InputType = {
  [PAYMENTS.CREDIT_CARD]: ({
    handleSubmit,
    values,
    setFieldValue,
    classes,
    normalizeExpiryField,
    handleCallback,
  }) => (
    <>
      <h3 style={{ marginBottom: '1rem' }}>{i18n.t('payments.credit_card_info')}</h3>
      <Cards
        cvc={values.cvc || ''}
        expiry={values.expiry || ''}
        focused={focus}
        issuer="visa|mastercard|discovery|amex"
        name={values.name || ''}
        number={values.number || ''}
        callback={({ issuer }) => handleCallback(issuer, setFieldValue)}
      />
      <Form noValidate onSubmit={handleSubmit} autoComplete="off">
        <Grid data-cy="add-payment-method" className={classes.paymentMethods} container spacing={1}>
          <Grid item xs={12}>
            <Field
              type="text"
              component={TextField}
              name="number"
              label={i18n.t('payments.card_number')}
              attrs={{
                maxLength: 16,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Field type="text" name="name" component={TextField} label={i18n.t('payments.name')} />
          </Grid>
          <Grid item xs={7}>
            <Field
              type="text"
              name="expiry"
              attrs={{
                maxLength: 7,
              }}
              onChange={normalizeExpiryField}
              component={TextField}
              label={i18n.t('payments.expiry')}
            />
          </Grid>
          <Grid item xs={5}>
            <Field
              type="text"
              name="cvc"
              component={TextField}
              label={i18n.t('payments.cvc')}
              attrs={{
                maxLength: 4,
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Field
              type="text"
              name="zipcode"
              component={TextField}
              label={i18n.t('payments.zip_code')}
              attrs={{
                maxLength: 9,
              }}
            />
          </Grid>
        </Grid>
      </Form>
    </>
  ),
  [PAYMENTS.ACH]: ({
    handleSubmit,
    values,
    setFieldValue,
    classes,
    formEl,
    disableFields,
    normalizeExpiryField,
  }) => {
    const holder_types = [
      { value: 'company', label: i18n.t('common.company') },
      { value: 'individual', label: i18n.t('common.individual') },
    ];
    return (
      <Form noValidate onSubmit={handleSubmit} autoComplete="off">
        <Grid data-cy="add-payment-method" className={classes.paymentMethods} container spacing={1}>
          <Grid item xs={12}>
            <h3 style={{ marginBottom: '1rem' }}>{i18n.t('payments.ach_info')}</h3>
          </Grid>
          <Grid item xs={12}>
            <Field
              type="text"
              name="account_holder_type"
              component={MaterialSelectField}
              placeholder={i18n.t('payments.holder_type')}
              options={holder_types}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Field
              type="text"
              name="account_holder_name"
              component={TextField}
              label={i18n.t('payments.holder_name')}
            />
          </Grid>
          <Grid item xs={12}>
            <Field
              type="text"
              name="routing_number"
              component={TextField}
              label={i18n.t('payments.router_number')}
            />
          </Grid>
          <Grid item xs={12}>
            <Field
              type="text"
              name="account_number"
              component={TextField}
              label={i18n.t('payments.account_number')}
            />
          </Grid>
        </Grid>
      </Form>
    );
  },
};

export { AddStripePaymentMethodModal };
